import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Save } from 'lucide-react';
import { API_URL } from './shared';
import { invalidatePageHeadersCache } from '../../hooks/usePageHeader';

const PAGES = [
  { slug: 'trips', label: 'Trips Page', route: '/trips' },
  { slug: 'courses', label: 'Courses Page', route: '/courses' },
  { slug: 'events', label: 'Events Page', route: '/events' },
  { slug: 'pre-dived', label: 'Pre-Dived Page', route: '/pre-owned' },
  { slug: 'new-gear', label: 'New Gear Page', route: '/products' },
  { slug: 'faq', label: 'FAQ Page', route: '/faq' },
];

const PageHeadersTab = ({ token }) => {
  const [headers, setHeaders] = useState({});
  const [savingSlug, setSavingSlug] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/admin/page-headers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (mounted) setHeaders(res.data || {}); })
      .catch(() => toast.error('Failed to load page headers'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const update = (slug, patch) => {
    setHeaders((prev) => ({ ...prev, [slug]: { ...(prev[slug] || {}), ...patch } }));
  };

  const save = async (slug) => {
    setSavingSlug(slug);
    try {
      const payload = headers[slug] || {};
      await axios.put(`${API_URL}/admin/page-headers/${slug}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      invalidatePageHeadersCache();
      toast.success(`${PAGES.find(p => p.slug === slug)?.label || slug} saved`);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSavingSlug(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-headers-tab">
      <div>
        <h2 className="text-lg font-semibold">Page Headers Editor</h2>
        <p className="text-sm text-gray-500">Edit the headline and intro text shown at the top of each public page.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {PAGES.map((p) => {
          const data = headers[p.slug] || { heading: '', subheading: '' };
          return (
            <div key={p.slug} className="bg-white rounded-lg border p-6 space-y-4" data-testid={`page-header-card-${p.slug}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-[#0A2342]">{p.label}</h3>
                <a href={p.route} target="_blank" rel="noreferrer" className="text-xs text-[#FF6B35] hover:underline">
                  View live →
                </a>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Heading</label>
                <Input
                  value={data.heading || ''}
                  onChange={(e) => update(p.slug, { heading: e.target.value })}
                  data-testid={`page-header-${p.slug}-heading`}
                />
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Subheading</label>
                <Input
                  value={data.subheading || ''}
                  onChange={(e) => update(p.slug, { subheading: e.target.value })}
                  data-testid={`page-header-${p.slug}-subheading`}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => save(p.slug)}
                  disabled={savingSlug === p.slug}
                  className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90"
                  data-testid={`page-header-${p.slug}-save`}
                >
                  {savingSlug === p.slug ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PageHeadersTab;
